// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState, useEffect } from 'react';

import { type MappedSpool, useEvolveDelete } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  spool: MappedSpool | undefined;
  onClose: () => void;
  refresh: () => void;
};

export const UnassignSpoolModal = ({ spool, onClose, refresh }: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder();
  const { apiCall: deleteItem, loading: deleting } = useEvolveDelete(`shop/workOrder/item/:workOrderItemId`);

  const opened = useMemo(() => isNotNil(spool), [spool]);
  const [spoolToUnassign, setSpoolToUnassign] = useState(spool);
  useEffect(() => {
    if (opened) setSpoolToUnassign(spool);
  }, [opened, spool]);

  const canUnassign = useMemo(
    () => (spool ? canEditWorkOrder('editItems', { workOrderStateName: spool.workOrderStateName }) : false),
    [canEditWorkOrder, spool],
  );

  return (
    <WrappedModal
      title="Unassign spool"
      confirmationText="Unassign"
      buttonColor="red"
      onConfirm={async () => {
        if (isNotNil(spoolToUnassign)) {
          await deleteItem({
            url: `shop/workOrder/item/${spoolToUnassign.workOrderItemId}`,
          })
            .then(refresh)
            .then(onClose);
        }
      }}
      opened={opened}
      onClose={onClose}
      loading={deleting}
      alertState={{
        title: 'Cannot unassign spool',
        showAlert: !canUnassign,
        message: isNotNil(spoolToUnassign) && (
          <Text>
            <b>{spoolToUnassign.spool}</b> cannot be unassigned from work order{' '}
            <b>{getDisplayNameWO(spoolToUnassign)}</b>. Instead, modify the corresponding work order.
          </Text>
        ),
      }}
    >
      {isNotNil(spoolToUnassign) && (
        <Text>
          Are you sure you want to unassign <b>{spoolToUnassign.spool}</b> from work order{' '}
          <b>{getDisplayNameWO(spoolToUnassign)}</b>?
        </Text>
      )}
    </WrappedModal>
  );
};
